import { Inject, Injectable } from '@angular/core';
import {
  AttendeeKeys,
  ChangeSourceType,
  GuestData,
  GuestEvents,
  GuestKeys,
  GuestStatus,
} from '@ag-common-lib/public-api';
import { FirebaseApp } from 'firebase/app';
import { FIREBASE_APP } from '../injections/firebase-app';
import { localDateTimeFromTimestamp } from '../../public-api';
import { CONFERENCES_COLLECTION_PATH } from './conference.service';
import { ToastrService } from 'ngx-toastr';
import { CONFERENCE_REGISTRANTS_COLLECTION_PATH } from './conference-registrants/conference-registrants.service';
import { catchError, map, Observable, throwError } from 'rxjs';
import { compareAsc } from 'date-fns';
import { normalizeFlightData } from '../utils/flight-data.utils';
import { normalizeSelectedExcursions } from '../utils/excursions-data.utils';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { CommonFireStoreDao, QueryParam, WhereFilterOperandKeys } from '../dao/CommonFireStoreDao.dao';

@Injectable({
  providedIn: 'root',
})
export class ConferenceGuestsService {
  readonly fsDao: CommonFireStoreDao<GuestData>;

  private readonly registrantGuestsCollectionPath = 'guests';

  constructor(
    @Inject(FIREBASE_APP) fireBaseApp: FirebaseApp,
    private toastrService: ToastrService,
  ) {
    this.fsDao = new CommonFireStoreDao<GuestData>(fireBaseApp, ConferenceGuestsService.fromFirestore, null);
  }

  static readonly fromFirestore = (data): GuestData => {
    const complimentaryConfiguration = data?.[GuestKeys.complimentaryConfiguration];
    const isMainEventComplimentary = data?.isComplimentary || complimentaryConfiguration?.[GuestEvents.main];
    const isChairmanExtensionComplimentary = complimentaryConfiguration?.[GuestEvents.chairmanExtension] ?? false;
    const guestEvents = data?.[GuestKeys.guestEvents] ?? [GuestEvents.main];

    return Object.assign({}, data, {
      [AttendeeKeys.dob]: localDateTimeFromTimestamp(data?.dob),
      [GuestKeys.guestStatus]: data?.[GuestKeys.guestStatus] ?? GuestStatus.default,
      [GuestKeys.guestEvents]: guestEvents,
      [GuestKeys.complimentaryConfiguration]: {
        [GuestEvents.main]: isMainEventComplimentary,
        [GuestEvents.chairmanExtension]: isChairmanExtensionComplimentary,
      },
      [GuestKeys.selectedExcursions]: normalizeSelectedExcursions(data?.[GuestKeys.selectedExcursions]),
      [GuestKeys.chairmanExtSelectedExcursions]: normalizeSelectedExcursions(
        data?.[GuestKeys.chairmanExtSelectedExcursions],
      ),
      [GuestKeys.flightInformation]: normalizeFlightData(data?.[GuestKeys.flightInformation] || {}),
    });
  };

  getList(conferenceDbId: string, registrantDbId: string, qp: QueryParam[] = []) {
    const table = ConferenceGuestsService.getCollectionPath(conferenceDbId, registrantDbId);

    return this.fsDao.getList(table, qp).pipe(
      map(guests => {
        guests.sort((left, right) => {
          const isLeftComplimentary = left?.[GuestKeys.complimentaryConfiguration]?.[GuestEvents.main];
          const isRightComplimentary = right?.[GuestKeys.complimentaryConfiguration]?.[GuestEvents.main];
          const isLeftCreatedDate = left?.[BaseModelKeys.createdDate];
          const isRightCreatedDate = right?.[BaseModelKeys.createdDate];
          if ((isLeftComplimentary && isRightComplimentary) || (!isLeftComplimentary && !isRightComplimentary)) {
            return compareAsc(isLeftCreatedDate, isRightCreatedDate);
          }

          if (isLeftComplimentary) {
            return -1;
          }

          return 1;
        });

        return guests;
      }),
    );
  }

  getConferenceGuestsByConferenceId(conferenceDbId: string): Observable<GuestData[]> {
    let qp: QueryParam[] = [];
    qp.push(new QueryParam(GuestKeys.conferenceDbId, WhereFilterOperandKeys.equal, conferenceDbId));

    return this.fsDao.getCollectionGroupSnapshot(this.registrantGuestsCollectionPath, qp).pipe(
      map(snapshot => {
        return snapshot.docs.map(doc => {
          if (!doc.exists()) {
            return null;
          }
          const data = doc.data();

          return data;
        });
      }),
    );
  }

  getGuestsByDbIdAndConferenceId(guestDbId: string, conferenceDbId: string): Observable<GuestData[]> {
    const qp: QueryParam[] = [];
    qp.push(new QueryParam(GuestKeys.conferenceDbId, WhereFilterOperandKeys.equal, conferenceDbId));
    qp.push(new QueryParam(BaseModelKeys.dbId, WhereFilterOperandKeys.equal, guestDbId));

    return this.fsDao.getCollectionGroupSnapshot(this.registrantGuestsCollectionPath, qp).pipe(
      map(snapshot => {
        return snapshot.docs
          .map(doc => {
            if (!doc.exists()) {
              return null;
            }
            const data = doc.data();
            return data;
          })
          .filter(doc => doc !== null); // Filter out null values
      }),
      catchError(error => {
        console.error('Error fetching guests:', error);
        // Handle your error appropriately
        return throwError(() => new Error('Error fetching guests.'));
      }),
    );
  }

  getDocumentData(conferenceDbId: string, registrantDbId: string, documentId: string): Observable<GuestData> {
    const table = ConferenceGuestsService.getCollectionPath(conferenceDbId, registrantDbId);

    return this.fsDao.getDocument(table, documentId).pipe(
      map(snapshot => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          return data;
        }
        return null;
      }),
    );
  }

  async create(
    conferenceDbId: string,
    registrantDbId: string,
    data: GuestData,
    silent = false,
    action: ChangeSourceType | null = null,
  ) {
    const table = ConferenceGuestsService.getCollectionPath(conferenceDbId, registrantDbId);

    Object.assign(data, {
      [GuestKeys.conferenceDbId]: conferenceDbId,
      [GuestKeys.registrantDbId]: registrantDbId,
      [BaseModelKeys.actionName]: action,
    });

    const registrant = await this.fsDao.create(data, table).catch(e => {
      // TODO add error toast
      console.log('e', e);
      throw new Error(e);
    });

    !silent && this.toastrService.success('Conference Registration Successfully Created!');

    return registrant;
  }

  async update(
    conferenceDbId: string,
    registrantDbId: string,
    documentId: string,
    updates: Partial<GuestData>,
    silent = false,
    action: ChangeSourceType | null = null,
  ) {
    Object.assign(updates, {
      [BaseModelKeys.actionName]: action,
    });
    const table = ConferenceGuestsService.getCollectionPath(conferenceDbId, registrantDbId);

    const registrant = await this.fsDao.updateFields(updates, documentId, table).catch(e => {
      console.log('e', e);
      this.toastrService.error('Conference Registration is Not Updated! Please try again later!');
      throw new Error(e);
    });

    !silent && this.toastrService.success('Conference Registration Successfully Updated!');

    return registrant;
  }

  delete(
    conferenceDbId: string,
    registrantDbId: string,
    documentId: string,
    silent = false,
    action: ChangeSourceType | null = null,
  ) {
    const table = ConferenceGuestsService.getCollectionPath(conferenceDbId, registrantDbId);

    return this.fsDao.delete(documentId, table, action).then(response => {
      !silent && this.toastrService.success('Agent Email Address Removed!');
      return response;
    });
  }

  static getCollectionPath(conferenceId: string, registrantDbId: string) {
    return [
      CONFERENCES_COLLECTION_PATH,
      conferenceId,
      CONFERENCE_REGISTRANTS_COLLECTION_PATH,
      registrantDbId,
      'guests',
    ].join('/');
  }
}
